import React, { useState, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import '../styles/ConfigPanel.css';

const VITA_CLASSIC = ['A1', 'A2', 'A3', 'A3.5', 'A4', 'B1', 'B2', 'B3', 'B4', 'C1', 'C2', 'C3', 'C4', 'D2', 'D3', 'D4'];

const VITA_3D_MASTER = [
  '0M1', '0M2', '0M3',
  '1M1', '1M2',
  '2L1.5', '2L2.5', '2M1', '2M2', '2M3', '2R1.5', '2R2.5',
  '3L1.5', '3L2.5', '3M1', '3M2', '3M3', '3R1.5', '3R2.5',
  '4L1.5', '4L2.5', '4M1', '4M2', '4M3', '4R1.5', '4R2.5',
  '5M1', '5M2', '5M3'
];

const BLEACH_SHADES = ['BL1', 'BL2', 'BL3', 'BL4', 'OM1', 'OM2', 'OM3'];

const VIVODENT_SHADES = [
  '01', '1A', '1C', '1D', '1E',
  '2A', '2B', '2C', '2E',
  '3A', '3C', '3E',
  '4A', '4B', '4C', '4D',
  '5A', '5B', '5C',
  '6B', '6C', '6D'
];

const ALL_PRESET_SHADES = [...VITA_CLASSIC, ...VITA_3D_MASTER, ...BLEACH_SHADES, ...VIVODENT_SHADES];

const ConfigPanel = ({ toothIds, config, updateConfig, isEditing, onCancel, onDraftChange }) => {
  const { products } = useData();
  const [isCustomShade, setIsCustomShade] = useState(false);
  const [localConfig, setLocalConfig] = useState({
    category: '',
    productId: '',
    name: '',
    details: {},
    notes: '',
    quantity: 1
  });

  const categories = [...new Set(products.map(p => p.category))];
  const toothKey = toothIds.slice().sort().join(',');

  // Initialize or reset local state ONLY when tooth selection or edit mode changes
  useEffect(() => {
    if (config && Object.keys(config).length > 0) {
      const derivedCategory = config.category || products.find(p => p.id === config.productId)?.category || '';
      const shadeVal = config.details?.teinte || '';
      setIsCustomShade(Boolean(shadeVal && !ALL_PRESET_SHADES.includes(shadeVal)));
      setLocalConfig({
        category: derivedCategory,
        productId: config.productId || '',
        name: config.name || '',
        details: config.details || {},
        notes: config.notes || '',
        quantity: config.quantity !== undefined && config.quantity !== null && !isNaN(Number(config.quantity))
          ? Number(config.quantity)
          : (toothIds.length || 1)
      });
    } else {
      setIsCustomShade(false);
      setLocalConfig({
        category: '',
        productId: '',
        name: '',
        details: {},
        notes: '',
        quantity: toothIds.length || 1
      });
    }
  }, [toothKey, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let updated;

    if (type === 'checkbox') {
      updated = {
        ...localConfig,
        details: {
          ...localConfig.details,
          [name]: checked
        }
      };
    } else if (name === 'category') {
      updated = {
        ...localConfig,
        category: value,
        productId: '',
        name: ''
      };
    } else if (name === 'productId') {
      const product = products.find(p => p.id === value);
      updated = {
        ...localConfig,
        productId: value,
        name: product ? product.name : ''
      };
    } else if (name === 'quantity') {
      const val = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0);
      updated = {
        ...localConfig,
        quantity: val
      };
    } else if (name.startsWith('details.')) {
      const detailName = name.split('.')[1];
      updated = {
        ...localConfig,
        details: {
          ...localConfig.details,
          [detailName]: value
        }
      };
    } else {
      updated = {
        ...localConfig,
        [name]: value
      };
    }

    setLocalConfig(updated);
    if (onDraftChange) {
      onDraftChange(updated);
    }
  };

  // Save configuration
  const handleSave = () => {
    const finalQuantity = localConfig.quantity === '' || localConfig.quantity === undefined
      ? (toothIds.length || 1)
      : Math.max(0, parseInt(localConfig.quantity, 10) || 0);
    updateConfig({
      ...localConfig,
      quantity: finalQuantity
    });
  };

  const currentProducts = products.filter(p => p.category === localConfig.category);

  const isArcadeMode = toothIds.some(t => t === 'HAUT' || t === 'BAS');
  const arcadeLabel = toothIds.map(t => t === 'HAUT' ? '⬆ Arcade Haut' : t === 'BAS' ? '⬇ Arcade Bas' : `dent ${t}`).join(' & ');

  const currentShade = localConfig.details.teinte || '';
  const selectShadeValue = !currentShade
    ? ''
    : ALL_PRESET_SHADES.includes(currentShade)
    ? currentShade
    : '__CUSTOM__';

  return (
    <div className="config-panel">
      <h3>Configuration {isArcadeMode ? `de l'${arcadeLabel}` : (toothIds.length > 1 ? `des dents ${toothIds.sort().join(', ')}` : `de la dent ${toothIds[0]}`)}</h3>

      <div className="config-section">
        <h4>Catégorie</h4>
        <div className="option-group">
          {categories.map(cat => (
            <div className="option-item" key={cat}>
              <input
                type="radio"
                id={`cat-${cat}`}
                name="category"
                value={cat}
                checked={localConfig.category === cat}
                onChange={handleInputChange}
              />
              <label htmlFor={`cat-${cat}`}>{cat}</label>
            </div>
          ))}
        </div>
      </div>

      {localConfig.category && (
        <div className="config-section">
          <h4>Prestation / Matériau</h4>
          <div className="option-group">
            {currentProducts.map(p => (
              <div className="option-item" key={p.id}>
                <input
                  type="radio"
                  id={`prod-${p.id}`}
                  name="productId"
                  value={p.id}
                  checked={localConfig.productId === p.id}
                  onChange={handleInputChange}
                />
                <label htmlFor={`prod-${p.id}`}>{p.name}</label>
              </div>
            ))}
          </div>
        </div>
      )}

      {localConfig.productId && (
        <div className="config-section">
          <h4>Détails additionnels</h4>
          <div className="details-group">
            <div className="detail-item">
              <div className="form-group">
                <label className="fw-semibold">Teinte</label>
                <select
                  name="details.teinte"
                  className="form-control mb-2"
                  value={selectShadeValue}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '__CUSTOM__') {
                      setIsCustomShade(true);
                      setLocalConfig(prev => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          teinte: ALL_PRESET_SHADES.includes(prev.details.teinte) ? '' : prev.details.teinte
                        }
                      }));
                    } else {
                      setIsCustomShade(false);
                      setLocalConfig(prev => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          teinte: val
                        }
                      }));
                    }
                  }}
                >
                  <option value="">Sélectionner une teinte...</option>
                  <optgroup label="VITA Classical">
                    {VITA_CLASSIC.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </optgroup>
                  <optgroup label="VITA 3D-Master">
                    {VITA_3D_MASTER.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Bleach / Extra Clair">
                    {BLEACH_SHADES.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Vivodent / Chromascop">
                    {VIVODENT_SHADES.map(t => (
                      <option key={`vivo-${t}`} value={t}>{t}</option>
                    ))}
                  </optgroup>
                  <option value="__CUSTOM__">Autre (Saisie libre...)</option>
                </select>

                {(isCustomShade || selectShadeValue === '__CUSTOM__') && (
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Entrez la teinte personnalisée (ex: 3M2 / 2M2, A2 au col...)"
                    value={localConfig.details.teinte || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setLocalConfig(prev => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          teinte: val
                        }
                      }));
                    }}
                  />
                )}
              </div>
            </div>

            <div className="detail-item mt-3">
              <div className="form-group">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <label className="m-0 fw-bold">Quantité</label>
                  <button
                    type="button"
                    className={`btn btn-sm ${Number(localConfig.quantity) === 0 ? 'btn-warning fw-bold text-dark' : 'btn-outline-secondary'}`}
                    style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px' }}
                    onClick={() => {
                      const newQty = Number(localConfig.quantity) === 0 ? (toothIds.length || 1) : 0;
                      setLocalConfig(prev => ({ ...prev, quantity: newQty }));
                    }}
                  >
                    {Number(localConfig.quantity) === 0 ? '🔄 À refaire (Qté 0 activée)' : '🔄 Mettre à 0 (Travail à refaire)'}
                  </button>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="number"
                    name="quantity"
                    className="form-control"
                    min="0"
                    value={localConfig.quantity !== undefined ? localConfig.quantity : (toothIds.length || 1)}
                    onChange={handleInputChange}
                    style={{ maxWidth: '120px' }}
                  />
                  {Number(localConfig.quantity) === 0 && (
                    <span className="badge bg-warning text-dark border p-2" style={{ fontSize: '11.5px' }}>
                      🔄 Non facturé (0,00 €)
                    </span>
                  )}
                </div>
                <small className="text-muted d-block mt-1">
                  {Number(localConfig.quantity) === 0
                    ? '⚠️ Cas sous garantie / travail à refaire : la quantité est à 0 pour ne pas facturer le praticien.'
                    : (isArcadeMode ? `Arcade sélectionnée : ${arcadeLabel}` : `Nombre de dents sélectionnées : ${toothIds.length}`)}
                </small>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="config-section">
        <h4>Notes techniques</h4>
        <textarea
          name="notes"
          className="form-control"
          rows="3"
          value={localConfig.notes}
          onChange={handleInputChange}
          placeholder="Informations supplémentaires..."
        ></textarea>
      </div>

      <div className="config-actions d-flex gap-2">
        <button className="btn btn-primary" onClick={handleSave}>
          {isEditing ? 'Mettre à jour la prestation' : 'Enregistrer la configuration'}
        </button>
        {isEditing && onCancel && (
          <button className="btn btn-outline-secondary" onClick={onCancel}>
            Annuler
          </button>
        )}
      </div>
    </div>
  );
};

export default ConfigPanel;
